import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg' | 'touch';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  loading?: boolean;
  block?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:   'bg-brand-500 text-white border border-brand-500 hover:bg-brand-600 active:bg-brand-700 disabled:bg-neutral-300 disabled:border-neutral-300 disabled:text-neutral-500',
  secondary: 'bg-neutral-0 text-neutral-800 border border-neutral-300 hover:bg-neutral-100 active:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-700',
  ghost:     'bg-transparent text-neutral-800 border border-transparent hover:bg-neutral-100 dark:text-neutral-100 dark:hover:bg-neutral-800',
  danger:    'bg-danger text-white border border-danger hover:opacity-90 active:opacity-80',
};

const SIZES: Record<Size, string> = {
  sm:    'h-8  px-3 text-xs',
  md:    'h-10 px-4 text-sm',
  lg:    'h-12 px-5 text-base',
  touch: 'h-touch min-w-[120px] px-6 text-md',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className = '', leadingIcon, trailingIcon, loading, block, disabled, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={[
        'ict-focus-ring inline-flex items-center justify-center gap-2 rounded font-medium transition-colors',
        VARIANTS[variant], SIZES[size],
        block ? 'w-full' : '',
        loading ? 'cursor-progress' : 'cursor-pointer',
        'disabled:cursor-not-allowed disabled:opacity-70',
        className,
      ].join(' ')}
      {...rest}
    >
      {leadingIcon ? <span className="shrink-0">{leadingIcon}</span> : null}
      <span>{children}</span>
      {trailingIcon ? <span className="shrink-0">{trailingIcon}</span> : null}
    </button>
  );
});
